import { useMemo, useState } from 'react';
import { HiMagnifyingGlass, HiOutlineUser, HiXMark } from 'react-icons/hi2';
import useContactStore from '../../stores/contactStore';

interface ContactSearchProps {
  onPick: (contactId: string, name: string) => void;
  onClose: () => void;
}

/** "Start a conversation": search contacts by name, email or phone (same matching as before). */
export default function ContactSearch({ onPick, onClose }: ContactSearchProps) {
  const contacts = useContactStore((s) => s.contacts);
  const [query, setQuery] = useState('');

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return contacts.filter((c) => {
      const fullName = `${c.first_name} ${c.last_name}`.toLowerCase();
      return fullName.includes(q) || (c.email?.toLowerCase() || '').includes(q) || (c.phone?.toLowerCase() || '').includes(q);
    });
  }, [contacts, query]);

  return (
    <div className="absolute inset-0 z-10 flex flex-col bg-white dark:bg-boxdark" role="search">
      <div className="flex-shrink-0 border-b border-stroke p-3 dark:border-strokedark">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-black dark:text-white">New conversation</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close search"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-bodydark1 hover:bg-gray-light focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:hover:bg-boxdark-2"
          >
            <HiXMark className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <label className="relative mt-2 block">
          <span className="sr-only">Search contacts</span>
          <HiMagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-bodydark2" aria-hidden />
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search contacts…"
            className="w-full rounded-lg border border-stroke bg-transparent py-2 pl-9 pr-3 text-base text-black outline-none focus:border-primary focus:ring-1 focus:ring-primary sm:text-sm dark:border-strokedark dark:text-white"
          />
        </label>
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto">
        {matches.map((c) => {
          const name = `${c.first_name} ${c.last_name}`.trim();
          return (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => c.id !== undefined && onPick(String(c.id), name)}
                className="flex w-full items-center gap-3 border-b border-stroke px-3 py-2.5 text-left hover:bg-gray-light focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary dark:border-strokedark dark:hover:bg-boxdark-2"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-light text-bodydark dark:bg-boxdark-2 dark:text-bodydark2" aria-hidden>
                  <HiOutlineUser className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-black dark:text-white">{name}</span>
                  {c.email && <span className="block truncate text-xs text-bodydark1 dark:text-bodydark2">{c.email}</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {!query.trim() && <p className="p-4 text-center text-xs text-bodydark1 dark:text-bodydark2">Type a name, email or phone to find a contact.</p>}
      {query.trim() && matches.length === 0 && <p className="p-4 text-center text-sm text-bodydark1 dark:text-bodydark2">No contacts found.</p>}
    </div>
  );
}
